import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useGetRunningTasksQuery } from '../../api/variantApi';
import { GetVariantsArgs } from '../../api/variantCheckApi';
import {
  RUNNING_TASK_POLLING_INTERVAL,
  RUNNING_TASK_TYPES,
  VARIANT_CREATION_TASK_TYPES,
} from '../../running_tasks/constants';

const PAGE_SIZE = 20;

type VariantCheckNavigationState = {
  pendingRefreshVariantId?: number;
  refreshTaskId?: string;
  refreshVariantId?: number;
};

export const useVariantCheckLogic = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationState = location.state as VariantCheckNavigationState | null;
  const pendingRefreshVariantId = navigationState?.pendingRefreshVariantId;
  const refreshTaskId = navigationState?.refreshTaskId;
  const refreshVariantId = navigationState?.refreshVariantId;

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);
  const [search, setSearch] = useState<undefined | string>(undefined);
  const [hasRecalculatingVariant, setHasRecalculatingVariant] = useState(false);

  const [queryArgs, setQueryArgs] = useState<GetVariantsArgs>({
    limit: PAGE_SIZE,
    offset: 0,
  });

  const { data: runningTasks = [] } = useGetRunningTasksQuery(undefined, {
    pollingInterval: RUNNING_TASK_POLLING_INTERVAL,
  });

  const trackedRefreshTask = refreshTaskId
    ? runningTasks.find((task) => task.task_id === refreshTaskId)
    : undefined;

  const activeRefreshVariantIds = useMemo(
    () =>
      new Set(
        runningTasks
          .filter(
            (task) =>
              task.task_type === RUNNING_TASK_TYPES.REFRESH_AND_RECALCULATE &&
              task.status === 'running' &&
              task.variant_id != null,
          )
          .map((task) => task.variant_id)
          .filter((variantId): variantId is number => variantId != null),
      ),
    [runningTasks],
  );

  // Variants that are still being created (variant_create /
  // vcf_variant_create tasks). While one of these is running the variants
  // list is polled so the "creating" row shows up — and clears — even if the
  // creation was started in another browser tab.
  const hasRunningCreationTasks = useMemo(
    () =>
      runningTasks.some(
        (task) =>
          VARIANT_CREATION_TASK_TYPES.includes(task.task_type) &&
          task.status === 'running',
      ),
    [runningTasks],
  );

  const trackedRefreshIsActive =
    pendingRefreshVariantId != null ||
    (refreshTaskId != null &&
      (!trackedRefreshTask || trackedRefreshTask.status === 'running'));

  const trackedVariantId = pendingRefreshVariantId ?? refreshVariantId;

  // Handle pending refresh task transition to running
  useEffect(() => {
    const startedRefreshTask = pendingRefreshVariantId
      ? runningTasks.find(
          (task) =>
            task.task_type === RUNNING_TASK_TYPES.REFRESH_AND_RECALCULATE &&
            task.variant_id === pendingRefreshVariantId &&
            task.status === 'running',
        )
      : undefined;

    if (startedRefreshTask) {
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: {
          refreshTaskId: startedRefreshTask.task_id,
          refreshVariantId: pendingRefreshVariantId,
        },
      });
    }
  }, [
    pendingRefreshVariantId,
    runningTasks,
    navigate,
    location.pathname,
    location.search,
  ]);

  // Handle refresh task completion
  useEffect(() => {
    if (
      trackedRefreshTask &&
      trackedRefreshTask.status !== 'running' &&
      refreshTaskId
    ) {
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: null,
      });
    }
  }, [
    trackedRefreshTask,
    refreshTaskId,
    navigate,
    location.pathname,
    location.search,
  ]);

  // Update recalculating variant state
  useEffect(() => {
    setHasRecalculatingVariant(
      trackedRefreshIsActive || activeRefreshVariantIds.size > 0,
    );
  }, [trackedRefreshIsActive, activeRefreshVariantIds]);

  const fetchData = useCallback(
    (
      offset = 0,
      query?: string,
      sortField?: string,
      sortOrder?: 'ascend' | 'descend',
      limit?: number,
    ) => {
      setQueryArgs({
        offset,
        limit: limit ?? pageSize,
        search: query ? query : search,
        sortField,
        sortOrder,
      });
    },
    [search, pageSize],
  );

  const handleSearchValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
  };

  const resetSearch = () => {
    setSearch('');
    setCurrentPage(1);
    setQueryArgs((previous) => ({ ...previous, offset: 0, search: undefined }));
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchData();
  };

  return {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    search,
    setSearch,
    queryArgs,
    setQueryArgs,
    hasRecalculatingVariant,
    hasRunningCreationTasks,
    trackedRefreshIsActive,
    activeRefreshVariantIds,
    trackedVariantId,
    fetchData,
    handleSearchValueChange,
    handleSearch,
    resetSearch,
    PAGE_SIZE,
  };
};
